/**
 * Host-level Vue Router.
 *
 * Phase 7 narrows the host router down to:
 *
 *   - the `/` redirect that lands on the calendar;
 *   - the `:pathMatch(.*)*` catch-all that bounces unknown URLs back to
 *     Calendar — same behavior as before;
 *   - a `scrollBehavior` that respects saved scroll positions and snaps
 *     to the top on brand-new navigations;
 *   - the `afterEach` title setter that drives `document.title` from each
 *     route's `meta.title`.
 *
 * Every other route (including `/:guildId/calendar`, `/:guildId/plan/:raidId`, `/:guildId/settings`,
 * `/templates`, `/onboarding`, `/my-builds`) is added at runtime by
 * `installGvgPlanner(app, router, …)` via `router.addRoute(record)`.
 * The host router stays UI-only and never imports feature code.
 *
 * The `selectRaid` cascade that triggers on `/:guildId/plan/:raidId` navigation
 * lives as a `beforeEach` global guard in
 * `apps/frontend/src/features/gvg-planner/routerGuards.ts`
 * (regression-tested by `plannerRaidNavigation.test.ts`).
 *
 * Locale reactivity: the `meta.title` for each route is the English
 * literal (kept stable so route-contract tests can match the exact
 * string). The `afterEach` hook resolves the title through the locale
 * catalog so the browser tab re-paints on every locale flip — both when
 * the user changes language and when the active guild flips the
 * automatic preference. The effective `<html lang>` is mirrored from
 * `localePort.locale` inside `App.vue`'s setup watcher; the title
 * resolver here shares the same locale source of truth.
 */
import { watchEffect } from 'vue'
import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
  type Router,
} from 'vue-router'

import { APP_ROUTE_NAMES } from '@/shared/constants/routes'
import { activeDiscordGuildId } from '@/app/guild'
import { localePort } from '@/app/locale'
import { defineLocaleCatalog } from '@/shared/i18n'
import { appHostEn } from '@/app/locales/en.ts'
import { appHostRu } from '@/app/locales/ru.ts'

const titleCatalog = defineLocaleCatalog({ en: appHostEn, ru: appHostRu })

/**
 * Resolve the human-readable title for a route record. `meta.title` is
 * treated as a lookup key into the locale catalog's `routeTitle` map;
 * the catalog's English copy keeps the original literal so route-contract
 * tests that pin `'My builds'` / `'Calendar'` still match the source.
 * Unknown keys fall back to the raw `meta.title` so a route declared
 * later (without an entry in the catalog) still surfaces a tab title.
 */
function resolveRouteTitle(
  routeName: string | symbol | null | undefined,
  metaTitle: string | undefined,
): string {
  if (routeName === null || routeName === undefined || typeof routeName !== 'string') {
    return metaTitle ?? 'WWM GvG'
  }
  const titles = titleCatalog[localePort.locale.value].routeTitle
  return titles[routeName as keyof typeof titles] ?? metaTitle ?? 'WWM GvG'
}

/**
 * Path-based redirect target for the root, legacy routes, and the catch-all
 * `/:pathMatch(.*)*` record.
 *
 * Redirects return string paths rather than named locations because the
 * catch-all route matches the `pathMatch` param internally. A path target
 * cannot accidentally carry that source-only parameter to the destination.
 */
function redirectToCalendar(): string {
  const guildId = activeDiscordGuildId.value
  return /^[1-9]\d{16,19}$/.test(guildId) ? `/${guildId}/calendar` : '/onboarding'
}

function redirectToSettings(): string {
  const guildId = activeDiscordGuildId.value
  return /^[1-9]\d{16,19}$/.test(guildId) ? `/${guildId}/settings` : '/onboarding'
}

function redirectToPlanner(raidId: string | string[] | undefined): string {
  const guildId = activeDiscordGuildId.value
  return typeof raidId === 'string'
    && /^[1-9]\d*$/.test(raidId)
    && /^[1-9]\d{16,19}$/.test(guildId)
    ? `/${guildId}/plan/${raidId}`
    : redirectToCalendar()
}

export function createHostRouter(): Router {
  const routes: RouteRecordRaw[] = [
    { path: '/', redirect: redirectToCalendar },
    { path: '/calendar', redirect: redirectToCalendar },
    { path: '/officers', redirect: redirectToSettings },
    { path: '/plan/:raidId', redirect: (to) => redirectToPlanner(to.params['raidId']) },
    {
      path: '/:pathMatch(.*)*',
      name: APP_ROUTE_NAMES.NotFound,
      redirect: redirectToCalendar,
    },
  ]

  const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
    scrollBehavior(_to, _from, savedPosition) {
      return savedPosition ?? { top: 0 }
    },
  })

  /**
   * `document.title` is bound to the current locale through a reactive
   * watcher: every effective-locale flip re-resolves the title without
   * waiting for the next navigation. The current route's `meta.title`
   * remains the English canonical key so route-contract tests stay
   * stable; the `routeTitle` lookup re-renders the visible string.
   *
   * The watcher is installed only after `router.isReady()` resolves so
   * the catch-all `/:pathMatch(.*)*` — which briefly becomes the
   * `currentRoute` value before the initial redirect lands — never
   * drives a locale flip mid-bootstrap. Without this gate, a locale
   * change that fires while `currentRoute.value.name === 'not-found'`
   * (and `metaTitle` is undefined) would write `Not found · WWM GvG`
   * onto the tab even though the user is one tick away from landing
   * on Calendar. The readiness signal is a router state, not a tip-off
   * timer — same `isReady()` promise Vue Router exposes to callers
   * that want to await the initial navigation.
   *
   * Real not-found routes (where a navigation guard intentionally
   * stops at the catch-all, or a future route is added with
   * `name: 'not-found'`) still paint `Not found · WWM GvG` because
   * they occur AFTER `isReady()` has resolved, when the watcher is
   * fully installed. The inside-watcher guard for the START location
   * (no `name`, no `meta.title`) is preserved as defensive code.
   */
  router.isReady().then(() => {
    watchEffect(() => {
      const current = router.currentRoute.value
      const metaTitle = typeof current.meta.title === 'string' ? current.meta.title : undefined
      if (metaTitle === undefined && (current.name === null || current.name === undefined)) {
        return
      }
      const localized = resolveRouteTitle(current.name, metaTitle)
      document.title = `${localized} · WWM GvG`
    })
  })

  router.afterEach((to) => {
    if (typeof to.meta.title !== 'string') return
    const localized = resolveRouteTitle(to.name, to.meta.title)
    document.title = `${localized} · WWM GvG`
  })

  return router
}